import { environment } from '@/environments/environment';

/**
 * Build an absolute URL for a stored shared-content asset. The store returns
 * realm-independent **relative** URLs (e.g. `/content/uploads/...`); some
 * consumers (the AI Assist service fetching a base plate) need an absolute,
 * fetchable URL. Mirrors the server's `ContentPolicy.BuildFullUrl`
 * (`https://{domain}{relativeUrl}`), resolving against the realm domain.
 *
 * Returns the input unchanged when it is already absolute, or null when there
 * is no domain / url to build from.
 */
export function absoluteSharedContentUrl(
    domain: string | null | undefined,
    relativeUrl: string | null | undefined,
): string | null {
    if (!relativeUrl) return null;
    if (/^https?:\/\//i.test(relativeUrl)) return relativeUrl;
    if (!domain) return null;
    const host = domain.replace(/\/+$/, '');
    const path = relativeUrl.startsWith('/') ? relativeUrl : `/${relativeUrl}`;
    return `https://${host}${path}`;
}

/**
 * Resolve a stored shared-content URL for preview in the admin. Stored values
 * are relative (`/content/uploads/...`), and the platform serves the store under
 * `/content` on the admin API host (realm-independent, behind the admin auth /
 * gateway — the bucket itself is private). So resolve against the admin API
 * origin (`environment.apiBaseUrl`):
 *  - already-absolute URLs (e.g. AI candidates) pass through unchanged;
 *  - when `apiBaseUrl` is absolute, prepend its origin
 *    (`https://{admin-host}/content/...`);
 *  - when `apiBaseUrl` is relative (dev / same-origin behind a gateway), return
 *    the relative `/content/...` as-is — it loads from the admin origin (the dev
 *    proxy and the prod gateway route `/content` to the platform).
 */
export function sharedContentPreviewUrl(url: string | null | undefined): string {
    if (!url) return '';
    if (/^https?:\/\//i.test(url)) return url;
    const path = url.startsWith('/') ? url : `/${url}`;
    const api = environment.apiBaseUrl;
    if (/^https?:\/\//i.test(api)) {
        return `${new URL(api).origin}${path}`;
    }
    return path;
}

/**
 * Short, deterministic, lowercase-hex token derived from a value — used to keep
 * a readable identifier (e.g. a site-template slug) out of public asset paths
 * while still grouping assets by it consistently. Not for security: it only
 * obfuscates, and SHA-256 truncation is collision-resistant enough for grouping.
 */
export async function hashPathToken(
    value: string,
    length = 16,
): Promise<string> {
    const bytes = new TextEncoder().encode(value);
    const digest = await crypto.subtle.digest('SHA-256', bytes);
    return Array.from(new Uint8Array(digest))
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('')
        .slice(0, length);
}
